// src/components/OptimizedImage.jsx
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Box } from "@mui/material";

const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className = "",
  loading = "lazy",
  sizes = "100vw",
  style = {},
  ...props
}) => {
  const [imageUrl, setImageUrl] = useState("");
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    const resolveImageSource = (source) => {
      if (!source) return "";

      // Se è un oggetto File o Blob
      if (source instanceof File || source instanceof Blob) {
        const url = URL.createObjectURL(source);
        setObjectUrl(url);
        return url;
      }

      // Se è un oggetto (come da import/require)
      if (typeof source === "object" && source !== null) {
        return source.default || source;
      }

      // Se è un URL assoluto o un data URL
      if (
        typeof source === "string" &&
        (source.startsWith("http") ||
          source.startsWith("//") ||
          source.startsWith("data:"))
      ) {
        return source;
      }

      // Se è un percorso relativo
      return source.startsWith("/") ? source : `/${source}`;
    };

    const url = resolveImageSource(src);
    setImageUrl(url);

    // Cleanup function per revocare l'URL quando il componente si smonta o l'immagine cambia
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
      }
    };
  }, [src, objectUrl]);

  if (!imageUrl) {
    return (
      <Box
        component="div"
        width={width}
        height={height}
        className={`image-placeholder ${className}`}
        sx={{
          backgroundColor: "grey.200",
          display: "inline-block",
          ...(width && height && { aspectRatio: `${width}/${height}` }),
          ...style,
        }}
      />
    );
  }

  return (
    <Box
      component="img"
      src={imageUrl}
      alt={alt || ""}
      width={width}
      height={height}
      loading={loading}
      sizes={sizes}
      className={className}
      style={{
        maxWidth: "100%",
        ...style,
      }}
      {...props}
    />
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  alt: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  loading: PropTypes.oneOf(["lazy", "eager"]),
  sizes: PropTypes.string,
  srcSet: PropTypes.string,
  quality: PropTypes.number,
  style: PropTypes.object,
};

export default OptimizedImage;
